import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { SEO } from "../components/SEO";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { MessageSquare, User, Clock, Send, Lock, Plus, Reply, ChevronDown, ChevronUp, Calendar, RefreshCw, Ticket, MessageCircle, ArrowLeft, Pin, Image, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

// Category icons mapping
const CategoryIcon = ({ category, className }) => {
  switch(category) {
    case 'matchday': return <Calendar className={className} />;
    case 'transfers': return <RefreshCw className={className} />;
    case 'tickets': return <Ticket className={className} />;
    default: return <MessageCircle className={className} />;
  }
};

// Category colors
const getCategoryColor = (category) => {
  switch(category) {
    case 'matchday': return { bg: 'bg-green-500', text: 'text-green-600', light: 'bg-green-50', border: 'border-green-200' };
    case 'transfers': return { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-200' };
    case 'tickets': return { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-200' };
    default: return { bg: 'bg-[#7C2C3B]', text: 'text-[#7C2C3B]', light: 'bg-rose-50', border: 'border-rose-200' };
  }
};

const ForumPostCard = ({ post, onReply, isExpanded, onToggle }) => {
  const { user, token } = useAuth();
  const [replyContent, setReplyContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const colors = getCategoryColor(post.category);

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/${post.id}/reply`, 
        { content: replyContent },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Reply posted!");
      setReplyContent("");
      onReply();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post reply");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl border ${colors.border} overflow-hidden hover:shadow-md transition-shadow`} data-testid={`forum-post-${post.id}`}>
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden">
            {post.user_profile_image ? (
              <img src={post.user_profile_image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className={`w-full h-full ${colors.bg} flex items-center justify-center`}>
                <User className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <span className="font-semibold text-[#0F172A]">{post.user_name}</span>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(post.created_at).toLocaleDateString('en-GB', { 
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                })}
              </span>
            </div>
            
            <h3 className="text-lg font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              {post.title}
            </h3>
            
            <p className="text-slate-600 text-sm whitespace-pre-wrap">{post.content}</p>
            
            {/* Post Image */}
            {post.image_url && !post.image_hidden && (
              <div className="mt-3">
                <img 
                  src={post.image_url} 
                  alt="Post image" 
                  className="max-w-full max-h-64 rounded-lg object-cover"
                  data-testid={`post-image-${post.id}`}
                />
              </div>
            )}
            
            {/* Replies */}
            <div className="mt-3 flex items-center gap-4">
              {post.replies && post.replies.length > 0 && (
                <button 
                  onClick={onToggle}
                  className={`flex items-center gap-1 text-sm ${colors.text} hover:opacity-80 transition-colors`}
                >
                  <MessageSquare className="w-4 h-4" />
                  {post.replies.length} {post.replies.length === 1 ? 'reply' : 'replies'}
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              )}
              
              {user && !isExpanded && (
                <button
                  onClick={onToggle}
                  className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1"
                >
                  <Reply className="w-4 h-4" />
                  Reply
                </button>
              )}
            </div>
            
            {isExpanded && (
              <div className="mt-4 space-y-3">
                {/* Existing replies */}
                {post.replies?.map((reply) => (
                  <div key={reply.id} className="bg-slate-50 rounded-lg p-3 border-l-2 border-slate-300">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                        {reply.user_profile_image ? (
                          <img src={reply.user_profile_image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-slate-400 flex items-center justify-center">
                            <User className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <span className="text-sm font-medium text-slate-700">{reply.user_name}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(reply.created_at).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 pl-8">{reply.content}</p>
                  </div>
                ))}
                
                {/* Reply input */}
                {user && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200">
                    <Input
                      placeholder="Write a reply..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      className="flex-1"
                      data-testid={`reply-input-${post.id}`}
                    />
                    <Button 
                      onClick={handleReply}
                      disabled={submitting || !replyContent.trim()}
                      size="sm"
                      className={`${colors.bg} hover:opacity-90`}
                      data-testid={`reply-btn-${post.id}`}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const NewPostDialog = ({ onSuccess, selectedCategory }) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState(selectedCategory || "general");
  const [submitting, setSubmitting] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useState(null);

  useEffect(() => {
    if (selectedCategory) {
      setCategory(selectedCategory);
    }
  }, [selectedCategory]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = `${API}/files/${res.data.storage_path}`;
      setImagePreview(url);
      setImageUrl(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setImageUrl("");
    setImagePreview(null);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/forum`, 
        { title, content, category, image_url: imageUrl || null },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Post created!");
      setTitle("");
      setContent("");
      setImageUrl("");
      setImagePreview(null);
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  const colors = getCategoryColor(category);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="new-post-btn">
          <Plus className="w-4 h-4 mr-2" />
          NEW POST
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>CREATE NEW POST</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {/* Category Selection */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Category</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'matchday', name: 'Match Day', icon: Calendar },
                { id: 'transfers', name: 'Transfers', icon: RefreshCw },
                { id: 'tickets', name: 'Tickets', icon: Ticket },
                { id: 'general', name: 'General', icon: MessageCircle }
              ].map((cat) => {
                const catColors = getCategoryColor(cat.id);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                      category === cat.id 
                        ? `${catColors.light} ${catColors.border} ${catColors.text}` 
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <cat.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700">Title</label>
            <Input
              placeholder="What's on your mind?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              data-testid="new-post-title"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Content</label>
            <Textarea
              placeholder="Share your thoughts with fellow Hammers..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              className="mt-1"
              data-testid="new-post-content"
            />
          </div>
          
          {/* Image Upload */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Add Image (optional)</label>
            {!imagePreview ? (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="post-image-upload"
                  data-testid="post-image-input"
                />
                <label 
                  htmlFor="post-image-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <Image className="w-5 h-5 text-slate-400" />
                  </div>
                  <span className="text-sm text-slate-500">
                    {uploading ? "Processing..." : "Click to upload image (max 5MB)"}
                  </span>
                </label>
              </div>
            ) : (
              <div className="relative">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full max-h-48 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <Button 
            onClick={handleSubmit}
            disabled={submitting || uploading}
            className={`w-full ${colors.bg} hover:opacity-90`}
            data-testid="submit-post-btn"
          >
            {submitting ? "POSTING..." : "POST"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Category Card Component
const CategoryCard = ({ category, onClick }) => {
  const colors = getCategoryColor(category.id);
  
  return (
    <button
      onClick={onClick}
      className={`w-full p-5 rounded-xl ${colors.light} border ${colors.border} hover:shadow-md transition-all text-left group`}
      data-testid={`forum-category-${category.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
          <CategoryIcon category={category.id} className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-lg font-bold ${colors.text} mb-1`} style={{ fontFamily: 'Barlow Condensed' }}>
            {category.name.toUpperCase()}
          </h3>
          <p className="text-sm text-slate-600 mb-2">{category.description}</p>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">{category.post_count} posts</span>
          </div>
        </div>
      </div>
    </button>
  );
};

export default function ForumPage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPost, setExpandedPost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchPosts(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/forum/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/forum?category=${category}`);
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setExpandedPost(null);
  };

  const handleBack = () => {
    setSelectedCategory(null);
    setPosts([]);
    fetchCategories();
  };

  const refreshData = () => {
    if (selectedCategory) {
      fetchPosts(selectedCategory);
    }
    fetchCategories();
  };

  const currentCategory = categories.find(c => c.id === selectedCategory);
  const colors = selectedCategory ? getCategoryColor(selectedCategory) : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="forum-page">
      <SEO title="Forum" path="/forum" description="West Ham United fan forum. Discuss match days, transfers, tactics and everything Hammers with fellow supporters." />
      <Navbar />
      
      {/* Header */}
      <div className={`${selectedCategory ? colors?.bg : 'bg-[#7C2C3B]'} py-10 transition-colors`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {selectedCategory && (
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to categories</span>
            </button>
          )}
          <div className="flex items-center gap-4">
            {selectedCategory && (
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <CategoryIcon category={selectedCategory} className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                {selectedCategory ? currentCategory?.name.toUpperCase() : 'FAN FORUM'}
              </h1>
              <p className="text-white/80 text-sm mt-1">
                {selectedCategory ? currentCategory?.description : 'Join the conversation with fellow Hammers'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Login Notice */}
      {!user && (
        <div className="bg-[#F8D742]/20 border-b border-[#F8D742]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-[#7C2C3B]" />
              <span className="text-[#7C2C3B] text-sm">
                Sign in to join the discussion. <Link to="/register" className="font-semibold underline">Join now</Link>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : !selectedCategory ? (
          /* Categories Grid */
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                CHOOSE A TOPIC
              </h2>
              <span className="text-sm text-slate-500">
                {categories.reduce((sum, c) => sum + c.post_count, 0)} total posts
              </span>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {categories.map(category => (
                <CategoryCard 
                  key={category.id} 
                  category={category}
                  onClick={() => handleCategorySelect(category.id)}
                />
              ))}
            </div>
          </div>
        ) : (
          /* Posts List */
          <div className="space-y-6">
            {/* New Post Button */}
            {user && (
              <div className="flex justify-end">
                <NewPostDialog onSuccess={refreshData} selectedCategory={selectedCategory} />
              </div>
            )}

            {posts.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <CategoryIcon category={selectedCategory} className={`w-16 h-16 mx-auto mb-4 ${colors?.text} opacity-30`} />
                <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  NO POSTS YET
                </h3>
                <p className="text-slate-500 mb-4">Be the first to start a discussion in {currentCategory?.name}!</p>
                {user && (
                  <NewPostDialog onSuccess={refreshData} selectedCategory={selectedCategory} />
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map(post => (
                  <ForumPostCard 
                    key={post.id} 
                    post={post} 
                    onReply={refreshData}
                    isExpanded={expandedPost === post.id}
                    onToggle={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
